import { createSelector } from 'reselect';
import _get from 'lodash/get';
import _set from 'lodash/set';
import {
  getBusinessDetails,
  getMappedCountryConfig,
  getDocuments,
  getStakeholders,
} from '@/selectors/merchant';
import { getOnboardingType } from '@/selectors/widget';

export const getDocumentRequirementsForBusiness = createSelector(
  getMappedCountryConfig,
  getBusinessDetails,
  getOnboardingType,
  ({ DOCUMENTS: { BUSINESS } = {} } = {}, { entityType, registered } = {}, onboardingType) =>
    Object.values({
      ..._get(BUSINESS, 'ALL', {}),
      ..._get(BUSINESS, `ENTITY_TYPE[${entityType}]`, {}),
      ..._get(BUSINESS, `REGISTRATION[${registered}]`, {}),
    }).map(({ requiredFor = [], ...rest }) => ({
      ...rest,
      required: requiredFor.includes(onboardingType),
    })),
);

export const getDocumentRequirementsForStakeholders = createSelector(
  getMappedCountryConfig,
  getStakeholders,
  getOnboardingType,
  ({ DOCUMENTS: { STAKEHOLDERS } = {} } = {}, stakeholders = [], onboardingType) =>
    stakeholders.map(stakeholder =>
      _set(
        { ...stakeholder },
        'documentRequirements',
        Object.values({
          ..._get(STAKEHOLDERS, 'ALL', {}),
          ...stakeholder.roles.reduce(
            (acc, role) => ({
              ...acc,
              ..._get(STAKEHOLDERS, `ROLES[${role}]`, {}),
            }),
            {},
          ),
        }).map(({ requiredFor = [], ...rest }) => ({
          ...rest,
          required: requiredFor.includes(onboardingType),
        })),
      ),
    ),
);

export const getStakeholdersWithUploadedDocuments = createSelector(
  getDocumentRequirementsForStakeholders,
  getDocuments,
  (stakeholders = [], documents = []) =>
    stakeholders.map((stakeholder = {}) => ({
      ...stakeholder,
      documents: documents.filter(
        // We need to stringify the stakeholderIds before comparing them to make sure they are
        // matching even if they have different types (string vs. number).
        ({ stakeholderId } = {}) => `${stakeholderId}` === `${stakeholder.id}`,
      ),
    })),
);

export const hasDocuments = createSelector(getDocuments, (documents = []) => !!documents.length);

export const shouldCollectDocuments = createSelector(
  getDocumentRequirementsForBusiness,
  getDocumentRequirementsForStakeholders,
  getMappedCountryConfig,
  getOnboardingType,
  (
    businessDocuments = [],
    stakeholdersWithDocuments = [],
    { DOCUMENTS: { ONBOARDING_TYPES = [] } = {} } = {},
    onboardingType,
  ) =>
    ONBOARDING_TYPES.includes(onboardingType) &&
    (businessDocuments.length > 0 ||
      stakeholdersWithDocuments.some(
        ({ documentRequirements = [] }) => documentRequirements.length > 0,
      )),
);

export const hasMandatoryDocuments = createSelector(
  shouldCollectDocuments,
  getDocumentRequirementsForBusiness,
  getDocumentRequirementsForStakeholders,
  (shouldCollectDocuments, businessDocuments = [], stakeholdersWithDocuments = []) =>
    shouldCollectDocuments &&
    (businessDocuments.some(d => d.required) ||
      stakeholdersWithDocuments.some(({ documentRequirements = [] }) =>
        documentRequirements.some(d => d.required),
      )),
);
